import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showKmModal, setShowKmModal] = useState(false);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAccidentModal, setShowAccidentModal] = useState(false);
  const [showAccidentsListModal, setShowAccidentsListModal] = useState(false);
  const [vehicleAccidents, setVehicleAccidents] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filter, setFilter] = useState({ status: '', vehicle_type: '' });
  const [alerts, setAlerts] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    license_plate: '',
    vehicle_type: 'car',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vin: '',
    fuel_type: 'gasoline',
    acquisition_type: 'lease',
    acquisition_date: '',
    monthly_cost: '',
    insurance_company: '',
    insurance_expiry: '',
    inspection_expiry: '',
    current_km: 0,
    notes: ''
  });

  const [assignData, setAssignData] = useState({ employee_id: '', notes: '' });
  const [kmData, setKmData] = useState({ date: '', km_reading: '', trip_purpose: '', origin: '', destination: '', distance: '', reimbursable: false });
  const [fuelData, setFuelData] = useState({ date: '', fuel_type: '', liters: '', cost: '', station: '', km_reading: '' });
  const [fineData, setFineData] = useState({ violation_date: '', violation_type: '', location: '', amount: '', paid_by: 'company' });
  const [maintenanceData, setMaintenanceData] = useState({ date: '', type: '', description: '', cost: '', provider: '', km_reading: '' });
  const [accidentData, setAccidentData] = useState({
    accident_date: new Date().toISOString().split('T')[0],
    accident_time: '',
    location: '',
    description: '',
    severity: 'minor',
    damage_description: '',
    repair_cost_estimate: '',
    insurance_claim: false,
    insurance_reference: '',
    police_report: false,
    police_reference: '',
    status: 'reported'
  });
  const [tenants, setTenants] = useState([]);

  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchVehicles();
    fetchEmployees();
    fetchAlerts();
    if (isSuperAdmin) fetchTenants();
  }, [filter]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/hr/alerts');
      const vehicleAlerts = response.data.alerts.filter(a => a.entity_type === 'vehicle');
      setAlerts(vehicleAlerts);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.vehicle_type) params.append('vehicle_type', filter.vehicle_type);
      
      const response = await api.get(`/hr/vehicles?${params.toString()}`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Erro ao carregar viaturas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/vehicles', formData);
      toast.success('Viatura criada com sucesso');
      setShowModal(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar viatura');
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta viatura?')) return;
    try {
      await api.delete(`/hr/vehicles/${vehicleId}`);
      toast.success('Viatura eliminada');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar viatura');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/hr/vehicles/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.imported} viaturas importadas com sucesso`);
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} linhas com erros`);
      }
      setShowImportModal(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar ficheiro');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/assign?employee_id=${assignData.employee_id}&notes=${encodeURIComponent(assignData.notes || '')}`);
      toast.success('Viatura atribuída');
      setShowAssignModal(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir viatura');
    }
  };

  const handleReturn = async (vehicleId, currentKm) => {
    try {
      await api.post(`/hr/vehicles/${vehicleId}/return?current_km=${currentKm}&condition=good`);
      toast.success('Viatura devolvida');
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao devolver viatura');
    }
  };

  const handleAddKm = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/km`, kmData);
      toast.success('Registo de Km adicionado');
      setShowKmModal(false);
      setKmData({ date: '', km_reading: '', trip_purpose: '', origin: '', destination: '', distance: '', reimbursable: false });
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao adicionar registo');
    }
  };

  const handleAddFuel = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/fuel`, fuelData);
      toast.success('Abastecimento registado');
      setShowFuelModal(false);
      setFuelData({ date: '', fuel_type: '', liters: '', cost: '', station: '', km_reading: '' });
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao registar abastecimento');
    }
  };

  const handleAddFine = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/fine`, fineData);
      toast.success('Coima registada');
      setShowFineModal(false);
      setFineData({ violation_date: '', violation_type: '', location: '', amount: '', paid_by: 'company' });
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao registar coima');
    }
  };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/maintenance`, maintenanceData);
      toast.success('Manutenção registada');
      setShowMaintenanceModal(false);
      setMaintenanceData({ date: '', type: '', description: '', cost: '', provider: '', km_reading: '' });
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao registar manutenção');
    }
  };

  const handleAddAccident = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/vehicles/${selectedVehicle.vehicle_id}/accident`, accidentData);
      toast.success('Sinistro registado com sucesso');
      setShowAccidentModal(false);
      setAccidentData({
        accident_date: new Date().toISOString().split('T')[0],
        accident_time: '',
        location: '',
        description: '',
        severity: 'minor',
        damage_description: '',
        repair_cost_estimate: '',
        insurance_claim: false,
        insurance_reference: '',
        police_report: false,
        police_reference: '',
        status: 'reported'
      });
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao registar sinistro');
    }
  };

  const handleViewAccidents = async (vehicle) => {
    try {
      const response = await api.get(`/hr/vehicles/${vehicle.vehicle_id}/accidents`);
      setVehicleAccidents(response.data.accidents || []);
      setSelectedVehicle(vehicle);
      setShowAccidentsListModal(true);
    } catch (error) {
      toast.error('Erro ao carregar sinistros');
    }
  };

  const handleUpdateAccidentStatus = async (accidentId, newStatus) => {
    try {
      await api.put(`/hr/vehicles/${selectedVehicle.vehicle_id}/accidents/${accidentId}`, { status: newStatus });
      toast.success('Estado do sinistro atualizado');
      handleViewAccidents(selectedVehicle);
      fetchVehicles();
    } catch (error) {
      toast.error('Erro ao atualizar sinistro');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: 'bg-emerald-100 text-emerald-700',
      assigned: 'bg-blue-100 text-blue-700',
      maintenance: 'bg-amber-100 text-amber-700',
      retired: 'bg-gray-100 text-gray-700'
    };
    const labels = { available: 'Disponível', assigned: 'Atribuída', maintenance: 'Manutenção', retired: 'Abatida' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status]}`}>{labels[status] || status}</span>;
  };

  const getEmployeeName = (id) => employees.find(e => e.employee_id === id)?.name || id;

  const getTotalFines = (vehicle) => {
    return (vehicle.fines_history || []).filter(f => f.status !== 'paid').reduce((sum, f) => sum + (f.amount || 0), 0);
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.vehicle_type) params.append('vehicle_type', filter.vehicle_type);
      
      const response = await api.get(`/hr/vehicles/export/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viaturas_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.vehicle_type) params.append('vehicle_type', filter.vehicle_type);
      
      const response = await api.get(`/hr/vehicles/export/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viaturas_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="vehicles-page" className="space-y-6 animate-fade-in">
      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-semibold text-amber-700 dark:text-amber-400">Alertas de Veículos ({alerts.length})</span>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert, idx) => (
              <div key={idx} className={`text-sm p-2 rounded ${alert.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700' : alert.priority === 'high' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700'}`}>
                <span className="font-medium">{alert.title}:</span> {alert.message}
              </div>
            ))}
            {alerts.length > 5 && <p className="text-xs text-amber-600">+{alerts.length - 5} mais alertas</p>}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Viaturas</h1>
          <p className="text-muted-foreground">Frota, Kms, Abastecimentos, Coimas e Manutenções</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            data-testid="export-vehicles-excel-btn"
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar para Excel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>
          <button
            onClick={handleExportPDF}
            data-testid="export-vehicles-pdf-btn"
            className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar para PDF"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            PDF
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 rounded-lg border border-input bg-background hover:bg-muted transition-colors flex items-center gap-2"
            data-testid="import-vehicles-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Importar
          </button>
          <button onClick={() => setShowModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Nova Viatura
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{vehicles.length}</p>
          <p className="text-xs text-muted-foreground">Total Frota</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{vehicles.filter(v => v.status === 'assigned').length}</p>
          <p className="text-xs text-muted-foreground">Atribuídas</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold text-amber-600">{vehicles.filter(v => v.inspection_expiry && new Date(v.inspection_expiry) < new Date(Date.now() + 30*24*60*60*1000)).length}</p>
          <p className="text-xs text-muted-foreground">Inspeção a vencer</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold text-red-600">{vehicles.reduce((sum, v) => sum + getTotalFines(v), 0).toLocaleString('pt-PT')}€</p>
          <p className="text-xs text-muted-foreground">Coimas Pendentes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="">Todos</option>
              <option value="available">Disponível</option>
              <option value="assigned">Atribuída</option>
              <option value="maintenance">Manutenção</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select value={filter.vehicle_type} onChange={(e) => setFilter({ ...filter, vehicle_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="">Todos</option>
              <option value="car">Automóvel</option>
              <option value="van">Carrinha</option>
              <option value="motorcycle">Mota</option>
              <option value="truck">Camião</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicles Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.length === 0 ? (
          <div className="col-span-full rounded-xl border bg-card p-8 text-center">
            <p className="text-muted-foreground">Sem viaturas registadas</p>
          </div>
        ) : (
          vehicles.map((vehicle) => (
            <div key={vehicle.vehicle_id} className="rounded-xl border bg-card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-2xl">🚗</span>
                  <h3 className="font-semibold">{vehicle.brand} {vehicle.model}</h3>
                  <p className="text-lg font-mono font-bold">{vehicle.license_plate}</p>
                </div>
                {getStatusBadge(vehicle.status)}
              </div>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Km Atual:</span>
                  <span className="font-medium">{(vehicle.current_km || 0).toLocaleString('pt-PT')} km</span>
                </div>
                {vehicle.assigned_to && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Atribuída a:</span>
                    <span className="font-medium">{getEmployeeName(vehicle.assigned_to)}</span>
                  </div>
                )}
                {vehicle.inspection_expiry && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Inspeção:</span>
                    <span className={new Date(vehicle.inspection_expiry) < new Date() ? 'text-red-600' : ''}>{vehicle.inspection_expiry}</span>
                  </div>
                )}
                {vehicle.insurance_expiry && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Seguro:</span>
                    <span>{vehicle.insurance_expiry}</span>
                  </div>
                )}
                {getTotalFines(vehicle) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Coimas Pendentes:</span>
                    <span className="font-medium">{getTotalFines(vehicle)}€</span>
                  </div>
                )}
                {(vehicle.accidents || []).length > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Sinistros:</span>
                    <span className="font-medium">{vehicle.accidents.length}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                {vehicle.status === 'available' && (
                  <button onClick={() => { setSelectedVehicle(vehicle); setShowAssignModal(true); }} className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">Atribuir</button>
                )}
                {vehicle.status === 'assigned' && (
                  <button onClick={() => handleReturn(vehicle.vehicle_id, vehicle.current_km)} className="px-2 py-1 rounded bg-amber-100 text-amber-700 text-xs font-medium">Devolver</button>
                )}
                <button onClick={() => { setSelectedVehicle(vehicle); setShowKmModal(true); }} className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium">+ Km</button>
                <button onClick={() => { setSelectedVehicle(vehicle); setShowFuelModal(true); }} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">+ Combustível</button>
                <button onClick={() => { setSelectedVehicle(vehicle); setShowFineModal(true); }} className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium">+ Coima</button>
                <button onClick={() => { setSelectedVehicle(vehicle); setShowAccidentModal(true); }} className="px-2 py-1 rounded bg-orange-100 text-orange-700 text-xs font-medium" data-testid={`add-accident-${vehicle.vehicle_id}`}>+ Sinistro</button>
                {(vehicle.accidents || []).length > 0 && (
                  <button onClick={() => handleViewAccidents(vehicle)} className="px-2 py-1 rounded bg-amber-100 text-amber-700 text-xs font-medium" data-testid={`view-accidents-${vehicle.vehicle_id}`}>📋 Ver Sinistros ({vehicle.accidents.length})</button>
                )}
                <button onClick={() => { setSelectedVehicle(vehicle); setShowMaintenanceModal(true); }} className="px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-medium">+ Manutenção</button>
                <button onClick={() => handleDelete(vehicle.vehicle_id)} className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-medium" data-testid={`delete-vehicle-${vehicle.vehicle_id}`}>Eliminar</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Vehicle Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Nova Viatura</h2></div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {/* Tenant selection for Super Admin */}
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={formData.tenant_id || ''}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar Tenant</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Matrícula</label>
                  <input type="text" value={formData.license_plate} onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select value={formData.vehicle_type} onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                    <option value="car">Automóvel</option>
                    <option value="van">Carrinha</option>
                    <option value="motorcycle">Mota</option>
                    <option value="truck">Camião</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Marca</label><input type="text" value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Modelo</label><input type="text" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Ano</label><input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div>
                  <label className="block text-sm font-medium mb-1">Combustível</label>
                  <select value={formData.fuel_type} onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                    <option value="gasoline">Gasolina</option>
                    <option value="diesel">Gasóleo</option>
                    <option value="electric">Elétrico</option>
                    <option value="hybrid">Híbrido</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo Aquisição</label>
                  <select value={formData.acquisition_type} onChange={(e) => setFormData({ ...formData, acquisition_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                    <option value="purchase">Compra</option>
                    <option value="lease">Leasing</option>
                    <option value="renting">Renting</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Custo Mensal (€)</label><input type="number" value={formData.monthly_cost} onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Seguradora</label><input type="text" value={formData.insurance_company} onChange={(e) => setFormData({ ...formData, insurance_company: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Fim Seguro</label><input type="date" value={formData.insurance_expiry} onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Fim Inspeção</label><input type="date" value={formData.inspection_expiry} onChange={(e) => setFormData({ ...formData, inspection_expiry: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Km Atual</label><input type="number" value={formData.current_km} onChange={(e) => setFormData({ ...formData, current_km: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">Criar Viatura</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Atribuir Viatura</h2><p className="text-sm text-muted-foreground">{selectedVehicle.license_plate} - {selectedVehicle.brand} {selectedVehicle.model}</p></div>
            <form onSubmit={handleAssign} className="p-6 space-y-4">
              <div><label className="block text-sm font-medium mb-1">Colaborador</label><select value={assignData.employee_id} onChange={(e) => setAssignData({ ...assignData, employee_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required><option value="">Selecionar</option>{employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Notas</label><textarea value={assignData.notes} onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" rows={2} /></div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowAssignModal(false)} className="px-4 py-2 rounded-lg border">Cancelar</button><button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Atribuir</button></div>
            </form>
          </div>
        </div>
      )}

      {/* KM Modal */}
      {showKmModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Registar Quilómetros</h2></div>
            <form onSubmit={handleAddKm} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Data</label><input type="date" value={kmData.date} onChange={(e) => setKmData({ ...kmData, date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Leitura Km</label><input type="number" value={kmData.km_reading} onChange={(e) => setKmData({ ...kmData, km_reading: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Origem</label><input type="text" value={kmData.origin} onChange={(e) => setKmData({ ...kmData, origin: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div><label className="block text-sm font-medium mb-1">Destino</label><input type="text" value={kmData.destination} onChange={(e) => setKmData({ ...kmData, destination: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div><label className="block text-sm font-medium mb-1">Motivo</label><input type="text" value={kmData.trip_purpose} onChange={(e) => setKmData({ ...kmData, trip_purpose: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div className="flex items-center gap-2"><input type="checkbox" checked={kmData.reimbursable} onChange={(e) => setKmData({ ...kmData, reimbursable: e.target.checked })} /><label className="text-sm">Reembolsável</label></div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowKmModal(false)} className="px-4 py-2 rounded-lg border">Cancelar</button><button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Registar</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Fuel Modal */}
      {showFuelModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Registar Abastecimento</h2></div>
            <form onSubmit={handleAddFuel} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Data</label><input type="date" value={fuelData.date} onChange={(e) => setFuelData({ ...fuelData, date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Km</label><input type="number" value={fuelData.km_reading} onChange={(e) => setFuelData({ ...fuelData, km_reading: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Litros</label><input type="number" step="0.01" value={fuelData.liters} onChange={(e) => setFuelData({ ...fuelData, liters: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Custo (€)</label><input type="number" step="0.01" value={fuelData.cost} onChange={(e) => setFuelData({ ...fuelData, cost: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Posto</label><input type="text" value={fuelData.station} onChange={(e) => setFuelData({ ...fuelData, station: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowFuelModal(false)} className="px-4 py-2 rounded-lg border">Cancelar</button><button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Registar</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Fine Modal */}
      {showFineModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Registar Coima</h2></div>
            <form onSubmit={handleAddFine} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Data Infração</label><input type="date" value={fineData.violation_date} onChange={(e) => setFineData({ ...fineData, violation_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Valor (€)</label><input type="number" value={fineData.amount} onChange={(e) => setFineData({ ...fineData, amount: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select value={fineData.violation_type} onChange={(e) => setFineData({ ...fineData, violation_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                  <option value="speeding">Excesso Velocidade</option>
                  <option value="parking">Estacionamento</option>
                  <option value="red_light">Sinal Vermelho</option>
                  <option value="other">Outro</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Local</label><input type="text" value={fineData.location} onChange={(e) => setFineData({ ...fineData, location: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div>
                <label className="block text-sm font-medium mb-1">Pago por</label>
                <select value={fineData.paid_by} onChange={(e) => setFineData({ ...fineData, paid_by: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                  <option value="company">Empresa</option>
                  <option value="employee">Colaborador</option>
                </select>
              </div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowFineModal(false)} className="px-4 py-2 rounded-lg border">Cancelar</button><button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Registar</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold">Registar Manutenção</h2></div>
            <form onSubmit={handleAddMaintenance} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Data</label><input type="date" value={maintenanceData.date} onChange={(e) => setMaintenanceData({ ...maintenanceData, date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required /></div>
                <div><label className="block text-sm font-medium mb-1">Custo (€)</label><input type="number" value={maintenanceData.cost} onChange={(e) => setMaintenanceData({ ...maintenanceData, cost: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select value={maintenanceData.type} onChange={(e) => setMaintenanceData({ ...maintenanceData, type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                  <option value="scheduled">Revisão</option>
                  <option value="repair">Reparação</option>
                  <option value="inspection">Inspeção</option>
                  <option value="tires">Pneus</option>
                  <option value="oil_change">Mudança Óleo</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Descrição</label><textarea value={maintenanceData.description} onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" rows={2} /></div>
              <div><label className="block text-sm font-medium mb-1">Fornecedor</label><input type="text" value={maintenanceData.provider} onChange={(e) => setMaintenanceData({ ...maintenanceData, provider: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowMaintenanceModal(false)} className="px-4 py-2 rounded-lg border">Cancelar</button><button type="submit" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground">Registar</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Importar Viaturas</h2>
              <p className="text-sm text-muted-foreground">Importe viaturas a partir de um ficheiro CSV ou Excel</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-lg border-2 border-dashed border-border p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-muted-foreground mb-4">Arraste um ficheiro ou clique para selecionar</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImport}
                  className="hidden"
                  id="import-vehicle-file"
                  data-testid="import-vehicle-file-input"
                />
                <label
                  htmlFor="import-vehicle-file"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      A importar...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Selecionar Ficheiro
                    </>
                  )}
                </label>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4 text-sm">
                <h4 className="font-medium mb-2">Formato esperado:</h4>
                <p className="text-muted-foreground mb-2">O ficheiro deve conter as seguintes colunas:</p>
                <code className="block text-xs bg-background p-2 rounded">
                  matricula;tipo;marca;modelo;ano;combustivel;aquisicao;custo_mensal;seguradora;seguro_expira;inspecao_expira;km;notas
                </code>
                <p className="text-xs text-muted-foreground mt-2">
                  Tipos válidos: car, van, motorcycle, truck
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Accident Modal */}
      {showAccidentModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Registar Sinistro</h2>
              <p className="text-sm text-muted-foreground mt-1">{selectedVehicle.brand} {selectedVehicle.model} - {selectedVehicle.license_plate}</p>
            </div>
            <form onSubmit={handleAddAccident} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Data *</label>
                  <input type="date" value={accidentData.accident_date} onChange={(e) => setAccidentData({...accidentData, accident_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" required />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Hora</label>
                  <input type="time" value={accidentData.accident_time} onChange={(e) => setAccidentData({...accidentData, accident_time: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Local *</label>
                <input type="text" value={accidentData.location} onChange={(e) => setAccidentData({...accidentData, location: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Av. da Liberdade, Lisboa" required />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Descrição do Sinistro *</label>
                <textarea value={accidentData.description} onChange={(e) => setAccidentData({...accidentData, description: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" rows={3} placeholder="Descreva como ocorreu o sinistro..." required />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Gravidade</label>
                <select value={accidentData.severity} onChange={(e) => setAccidentData({...accidentData, severity: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                  <option value="minor">Ligeiro</option>
                  <option value="moderate">Moderado</option>
                  <option value="major">Grave</option>
                  <option value="total_loss">Perda Total</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Descrição dos Danos</label>
                <textarea value={accidentData.damage_description} onChange={(e) => setAccidentData({...accidentData, damage_description: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" rows={2} placeholder="Descreva os danos no veículo..." />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Estimativa de Reparação (€)</label>
                <input type="number" step="0.01" value={accidentData.repair_cost_estimate} onChange={(e) => setAccidentData({...accidentData, repair_cost_estimate: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="0.00" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={accidentData.police_report} onChange={(e) => setAccidentData({...accidentData, police_report: e.target.checked})} />
                  <span className="text-sm">Participação Policial</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={accidentData.insurance_claim} onChange={(e) => setAccidentData({...accidentData, insurance_claim: e.target.checked})} />
                  <span className="text-sm">Participação ao Seguro</span>
                </label>
              </div>
              {accidentData.police_report && (
                <div>
                  <label className="text-sm text-muted-foreground">Nº Participação Policial</label>
                  <input type="text" value={accidentData.police_reference} onChange={(e) => setAccidentData({...accidentData, police_reference: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
                </div>
              )}
              {accidentData.insurance_claim && (
                <div>
                  <label className="text-sm text-muted-foreground">Nº Sinistro Seguro</label>
                  <input type="text" value={accidentData.insurance_reference} onChange={(e) => setAccidentData({...accidentData, insurance_reference: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <button type="button" onClick={() => setShowAccidentModal(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">Cancelar</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-500" data-testid="submit-accident">Registar Sinistro</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Accidents List Modal */}
      {showAccidentsListModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Histórico de Sinistros</h2>
              <p className="text-sm text-muted-foreground mt-1">{selectedVehicle.brand} {selectedVehicle.model} - {selectedVehicle.license_plate}</p>
            </div>
            <div className="p-6">
              {vehicleAccidents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Sem sinistros registados</p>
              ) : (
                <div className="space-y-4">
                  {vehicleAccidents.map((accident, idx) => (
                    <div key={accident.accident_id || idx} className={`p-4 rounded-lg border ${
                      accident.status === 'resolved' ? 'bg-emerald-50 border-emerald-200' :
                      accident.status === 'under_review' ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium">{accident.accident_date} {accident.accident_time && `às ${accident.accident_time}`}</p>
                          <p className="text-sm text-muted-foreground">{accident.location}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            accident.severity === 'minor' ? 'bg-blue-100 text-blue-700' :
                            accident.severity === 'moderate' ? 'bg-amber-100 text-amber-700' :
                            accident.severity === 'major' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {accident.severity === 'minor' ? 'Ligeiro' :
                             accident.severity === 'moderate' ? 'Moderado' :
                             accident.severity === 'major' ? 'Grave' :
                             accident.severity === 'total_loss' ? 'Perda Total' : accident.severity}
                          </span>
                          <select
                            value={accident.status}
                            onChange={(e) => handleUpdateAccidentStatus(accident.accident_id, e.target.value)}
                            className="text-xs px-2 py-1 rounded border border-input bg-background"
                          >
                            <option value="reported">Reportado</option>
                            <option value="under_review">Em Análise</option>
                            <option value="resolved">Resolvido</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-sm mb-2">{accident.description}</p>
                      {accident.damage_description && (
                        <p className="text-sm text-muted-foreground mb-2"><strong>Danos:</strong> {accident.damage_description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {accident.repair_cost_estimate && (
                          <span><strong>Est. Reparação:</strong> {accident.repair_cost_estimate}€</span>
                        )}
                        {accident.police_report && (
                          <span className="text-blue-600">📋 Participação Policial {accident.police_reference && `(${accident.police_reference})`}</span>
                        )}
                        {accident.insurance_claim && (
                          <span className="text-purple-600">🛡️ Sinistro Seguro {accident.insurance_reference && `(${accident.insurance_reference})`}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-border flex justify-end">
              <button onClick={() => setShowAccidentsListModal(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;
